import { Map, YMaps } from '@pbe/react-yandex-maps'
import { useEffect, useRef, useState } from 'react'

import { Shop } from '@/widgets/ShopsMapNew/types/shop'
import { ShopsList } from '@/widgets/ShopsMapNew/ui/ShopsList/ShopsList'

import { useRegion } from '@/entities/regions'

import { useMedia } from '@/shared/lib/hooks/useMedia'

import { ShopPlacemark } from '../ShopPlacemark/ShopPlacemark'

import cl from './ShopsMap.module.scss'

const SPB_CENTER = [59.964782100421665, 30.345255046874975]

interface ShopsMapProps {
	shops?: Shop[]
	onSelect?: (value: Shop) => void
	activeShopID?: string
}

function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
	function deg2rad(deg: number) {
		return deg * (Math.PI / 180)
	}

	const R = 6371 // Radius of the earth in km
	const dLat = deg2rad(lat2 - lat1)
	const dLon = deg2rad(lon2 - lon1)
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
	const distance = R * c // Distance in km
	return distance
}

export const ShopsMap = (props: ShopsMapProps) => {
	const { shops = [], activeShopID, onSelect } = props
	const { isLargerThan } = useMedia()
	const isDesktop = isLargerThan(`992px`)
	const {
		currentRegion: { REGION_CODE }
	} = useRegion()
	const zoomControlRef = useRef<any>(null)

	const [yMap, setYMap] = useState<any>(null)
	const [activeBalloonId, setActiveBalloonId] = useState<number | null>(null)
	const [scrollToShop, setScrollToShop] = useState<string | null>(null)

	const defaultCenter = REGION_CODE === 'spb' ? SPB_CENTER : [+shops?.[0]?.GPS_N, +shops?.[0]?.GPS_S]

	// Создаем отдельные функции для обработки выбора из списка и клика по метке
	const handleSelectFromList = (shop: Shop) => {
		const map = zoomControlRef.current

		const currentMapCenter = map?.getCenter()
		const currentMapZoom = map?.getZoom()

		const distanceThreshold = 30
		const adjustedDistanceThreshold = distanceThreshold / (currentMapZoom || 1)

		const currentLat = currentMapCenter[0]
		const currentLon = currentMapCenter[1]
		if (getDistanceFromLatLonInKm(currentLat, currentLon, +shop.GPS_N, +shop.GPS_S) > adjustedDistanceThreshold) {
			const isMobile = window.matchMedia('(max-width: 576px)').matches
			const adjusted_GPS_N = isMobile ? +shop.GPS_N + 0.05 : +shop.GPS_N

			map?.setCenter([adjusted_GPS_N, shop.GPS_S], 17)
		}

		onSelect?.(shop)
		setActiveBalloonId(+shop.STORE_ID)
		setScrollToShop(shop.STORE_ID)
	}

	const handleSelectFromMap = (shop: Shop) => {
		// Для мобильной версии не изменяем масштаб и центр карты
		const isMobile = window.matchMedia('(max-width: 992px)').matches

		if (!isMobile) {
			// На десктопе работаем как раньше
			const map = zoomControlRef.current
			const currentMapCenter = map?.getCenter()
			const currentMapZoom = map?.getZoom()

			const distanceThreshold = 30
			const adjustedDistanceThreshold = distanceThreshold / (currentMapZoom || 1)

			const currentLat = currentMapCenter[0]
			const currentLon = currentMapCenter[1]
			if (getDistanceFromLatLonInKm(currentLat, currentLon, +shop.GPS_N, +shop.GPS_S) > adjustedDistanceThreshold) {
				const adjusted_GPS_N = +shop.GPS_N

				map?.setCenter([adjusted_GPS_N, shop.GPS_S], 17)
			}
		}

		onSelect?.(shop)
		setActiveBalloonId(+shop.STORE_ID)
		setScrollToShop(shop.STORE_ID)
	}

	useEffect(() => {
		const map = zoomControlRef.current

		if (map) {
			map.events.add('click', function () {
				setActiveBalloonId(null)
			})
		}

		return () => {
			map?.events.remove('click')
		}
	}, [yMap])

	return (
		<div className={cl.root} id="productMap">
			{isDesktop && (
				<ShopsList
					shops={shops}
					activeValue={activeShopID}
					onSelect={handleSelectFromList} // Используем функцию для выбора из списка
					hideRadio
					scrollToShop={scrollToShop}
				/>
			)}
			<YMaps>
				<Map
					className={cl.map}
					instanceRef={zoomControlRef}
					onLoad={yMap => setYMap(yMap)}
					modules={['templateLayoutFactory']}
					defaultState={{
						center: defaultCenter,
						zoom: 10
					}}>
					{shops.map(shop => (
						<ShopPlacemark
							key={shop.NAME}
							shop={shop}
							onSelect={() => handleSelectFromMap(shop)} // Используем функцию для выбора с карты
							yMap={yMap}
							isSelected={activeShopID === shop.STORE_ID}
							isActive={activeBalloonId === +shop.STORE_ID}
						/>
					))}
				</Map>
			</YMaps>
		</div>
	)
}
